import React, { useState, useEffect } from 'react';
import {
    Card,
    Text,
    Button,
    Field,
    Input,
    Dropdown,
    Option,
    Badge,
    Dialog,
    DialogSurface,
    DialogBody,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogTrigger,
    Spinner,
    MessageBar,
    MessageBarBody,
    Switch,
    Textarea,
} from '@fluentui/react-components';
import {
    Calendar24Regular,
    Add24Regular,
    Delete24Regular,
    Edit24Regular,
    Clock24Regular,
    Warning24Regular,
    Checkmark24Regular,
    Person24Regular,
} from '@fluentui/react-icons';
import { apiService } from '../../../services/api/apiService';

interface AvailabilityBlock {
    id: string;
    date: string;
    startTime: string;
    endTime: string;
    type: 'available' | 'unavailable' | 'limited';
    reason?: string;
    maxConsultations?: number;
    notes?: string;
}

interface TimeOffRequest {
    id: string;
    startDate: string;
    endDate: string;
    reason: string;
    status: 'pending' | 'approved' | 'denied';
    type: 'vacation' | 'sick' | 'conference' | 'other';
}

export const DoctorAvailability: React.FC = () => {
    const [selectedDate, setSelectedDate] = useState<Date>(new Date());
    const [availabilityBlocks, setAvailabilityBlocks] = useState<AvailabilityBlock[]>([]);
    const [timeOffRequests, setTimeOffRequests] = useState<TimeOffRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [showTimeOffDialog, setShowTimeOffDialog] = useState(false);
    const [editingBlock, setEditingBlock] = useState<AvailabilityBlock | null>(null);
    const [message, setMessage] = useState<{
        type: 'success' | 'error' | 'warning';
        text: string;
    } | null>(null);

    const [newBlock, setNewBlock] = useState({
        date: '',
        startTime: '08:00',
        endTime: '17:00',
        type: 'available' as 'available' | 'unavailable' | 'limited',
        reason: '',
        maxConsultations: 8,
        notes: '',
    });

    const [newTimeOff, setNewTimeOff] = useState({
        startDate: '',
        endDate: '',
        reason: '',
        type: 'vacation' as const,
    });

    const availabilityTypes = [
        { value: 'available', label: 'Available', color: 'success' },
        { value: 'unavailable', label: 'Unavailable', color: 'danger' },
        { value: 'limited', label: 'Limited Availability', color: 'warning' },
    ];

    const timeOffTypes = [
        { value: 'vacation', label: 'Vacation' },
        { value: 'sick', label: 'Sick Leave' },
        { value: 'conference', label: 'Conference/Training' },
        { value: 'other', label: 'Other' },
    ];

    useEffect(() => {
        loadAvailabilityData();
    }, [selectedDate]);

    const loadAvailabilityData = async () => {
        try {
            setLoading(true);
            const startDate = new Date(selectedDate);
            startDate.setDate(startDate.getDate() - 30);
            const endDate = new Date(selectedDate);
            endDate.setDate(endDate.getDate() + 30);

            // In a real implementation, this would fetch actual availability data
            // For now, generate some demo data
            const demoBlocks: AvailabilityBlock[] = [
                {
                    id: '1',
                    date: new Date().toISOString().split('T')[0],
                    startTime: '08:00',
                    endTime: '12:00',
                    type: 'limited',
                    reason: 'Morning only - afternoon training',
                    maxConsultations: 4,
                    notes: 'Available for urgent cases only in the afternoon',
                },
                {
                    id: '2',
                    date: new Date(Date.now() + 86400000).toISOString().split('T')[0],
                    startTime: '09:00',
                    endTime: '17:00',
                    type: 'available',
                    maxConsultations: 8,
                },
            ];

            const demoTimeOff: TimeOffRequest[] = [
                {
                    id: '1',
                    startDate: new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0],
                    endDate: new Date(Date.now() + 9 * 86400000).toISOString().split('T')[0],
                    reason: 'Family vacation',
                    status: 'approved',
                    type: 'vacation',
                },
            ];

            setAvailabilityBlocks(demoBlocks);
            setTimeOffRequests(demoTimeOff);
        } catch (error) {
            console.error('Error loading availability data:', error);
            setMessage({ type: 'error', text: 'Failed to load availability data' });
        } finally {
            setLoading(false);
        }
    };

    const handleAddAvailability = async () => {
        try {
            if (!newBlock.date || !newBlock.startTime || !newBlock.endTime) {
                setMessage({ type: 'error', text: 'Please fill in all required fields' });
                return;
            }

            const block: AvailabilityBlock = {
                id: Date.now().toString(),
                ...newBlock,
            };

            setAvailabilityBlocks((prev) => [...prev, block]);
            setNewBlock({
                date: '',
                startTime: '08:00',
                endTime: '17:00',
                type: 'available',
                reason: '',
                maxConsultations: 8,
                notes: '',
            });
            setShowAddDialog(false);
            setMessage({ type: 'success', text: 'Availability block added successfully' });
        } catch (error) {
            console.error('Error adding availability:', error);
            setMessage({ type: 'error', text: 'Failed to add availability block' });
        }
    };

    const handleUpdateAvailability = async () => {
        if (!editingBlock) return;

        try {
            setAvailabilityBlocks((prev) =>
                prev.map((block) => (block.id === editingBlock.id ? editingBlock : block))
            );
            setEditingBlock(null);
            setMessage({ type: 'success', text: 'Availability updated successfully' });
        } catch (error) {
            console.error('Error updating availability:', error);
            setMessage({ type: 'error', text: 'Failed to update availability' });
        }
    };

    const handleDeleteAvailability = async (blockId: string) => {
        try {
            setAvailabilityBlocks((prev) => prev.filter((block) => block.id !== blockId));
            setMessage({ type: 'success', text: 'Availability block deleted' });
        } catch (error) {
            console.error('Error deleting availability:', error);
            setMessage({ type: 'error', text: 'Failed to delete availability block' });
        }
    };

    const handleRequestTimeOff = async () => {
        try {
            if (!newTimeOff.startDate || !newTimeOff.endDate || !newTimeOff.reason) {
                setMessage({ type: 'error', text: 'Please fill in all required fields' });
                return;
            }

            const request: TimeOffRequest = {
                id: Date.now().toString(),
                ...newTimeOff,
                status: 'pending',
            };

            setTimeOffRequests((prev) => [...prev, request]);
            setNewTimeOff({
                startDate: '',
                endDate: '',
                reason: '',
                type: 'vacation',
            });
            setShowTimeOffDialog(false);
            setMessage({ type: 'success', text: 'Time off request submitted' });
        } catch (error) {
            console.error('Error requesting time off:', error);
            setMessage({ type: 'error', text: 'Failed to submit time off request' });
        }
    };

    const getDateAvailability = (date: Date) => {
        const dateStr = date.toISOString().split('T')[0];
        const blocks = availabilityBlocks.filter((block) => block.date === dateStr);
        const timeOff = timeOffRequests.find(
            (req) => req.status === 'approved' && dateStr >= req.startDate && dateStr <= req.endDate
        );

        if (timeOff) return { type: 'unavailable', reason: 'Time off' };
        if (blocks.length === 0) return { type: 'default', reason: 'Standard hours' };
        if (blocks.some((b) => b.type === 'unavailable'))
            return { type: 'unavailable', reason: 'Not available' };
        if (blocks.some((b) => b.type === 'limited'))
            return { type: 'limited', reason: 'Limited availability' };
        return { type: 'available', reason: 'Available' };
    };

    const formatDate = (date: string) => {
        return new Date(date).toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    const getStatusBadge = (status: string) => {
        const config = {
            pending: { color: 'warning', text: 'Pending' },
            approved: { color: 'success', text: 'Approved' },
            denied: { color: 'danger', text: 'Denied' },
        };
        const { color, text } = config[status as keyof typeof config] || config.pending;
        return (
            <Badge appearance="tint" color={color as any}>
                {text}
            </Badge>
        );
    };

    if (loading) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '400px',
                }}
            >
                <Spinner size="large" label="Loading availability..." />
            </div>
        );
    }

    return (
        <div style={{ padding: '0' }}>
            {/* Header */}
            <div style={{ marginBottom: '24px' }}>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                    }}
                >
                    <div>
                        <Text size={900} weight="bold">
                            Availability Management
                        </Text>
                        <Text
                            size={300}
                            style={{ display: 'block', color: '#605e5c', marginTop: '4px' }}
                        >
                            Manage your availability schedule and time off requests
                        </Text>
                    </div>

                    <div style={{ display: 'flex', gap: '12px' }}>
                        <Dialog>
                            <DialogTrigger disableButtonEnhancement>
                                <Button
                                    appearance="secondary"
                                    icon={<Person24Regular />}
                                    onClick={() => setShowTimeOffDialog(true)}
                                >
                                    Request Time Off
                                </Button>
                            </DialogTrigger>
                        </Dialog>

                        <Dialog>
                            <DialogTrigger disableButtonEnhancement>
                                <Button
                                    appearance="primary"
                                    icon={<Add24Regular />}
                                    onClick={() => setShowAddDialog(true)}
                                >
                                    Add Availability
                                </Button>
                            </DialogTrigger>
                        </Dialog>
                    </div>
                </div>

                {message && (
                    <div style={{ marginTop: '12px' }}>
                        <MessageBar
                            intent={
                                message.type === 'success'
                                    ? 'success'
                                    : message.type === 'error'
                                    ? 'error'
                                    : 'warning'
                            }
                        >
                            <MessageBarBody>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        width: '100%',
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        {message.type === 'success' && (
                                            <Checkmark24Regular style={{ marginRight: '8px' }} />
                                        )}
                                        {message.text}
                                    </div>
                                    <Button
                                        appearance="transparent"
                                        size="small"
                                        onClick={() => setMessage(null)}
                                        style={{ minWidth: 'auto', padding: '4px' }}
                                    >
                                        ×
                                    </Button>
                                </div>
                            </MessageBarBody>
                        </MessageBar>
                    </div>
                )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                {/* Calendar Overview */}
                <Card style={{ padding: '20px', height: 'fit-content' }}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            marginBottom: '16px',
                        }}
                    >
                        <Calendar24Regular style={{ fontSize: '20px', color: '#0078d4' }} />
                        <Text size={600} weight="semibold">
                            Calendar Overview
                        </Text>
                    </div>

                    <Field label="Select Date">
                        <Input
                            type="date"
                            value={selectedDate.toISOString().split('T')[0]}
                            onChange={(e) => setSelectedDate(new Date(e.target.value))}
                            style={{ width: '100%' }}
                        />
                    </Field>

                    <div
                        style={{
                            marginTop: '16px',
                            padding: '12px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '4px',
                        }}
                    >
                        <Text size={300}>
                            Date availability will be shown here based on your selected date.
                        </Text>
                    </div>

                    <div style={{ marginTop: '16px' }}>
                        <Text size={500} weight="medium" style={{ marginBottom: '12px' }}>
                            Legend
                        </Text>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div
                                    style={{
                                        width: '16px',
                                        height: '16px',
                                        backgroundColor: '#f0f9f0',
                                        border: '1px solid #10b981',
                                        borderRadius: '4px',
                                    }}
                                />
                                <Text size={300}>Available</Text>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div
                                    style={{
                                        width: '16px',
                                        height: '16px',
                                        backgroundColor: '#fefbf2',
                                        border: '1px solid #f59e0b',
                                        borderRadius: '4px',
                                    }}
                                />
                                <Text size={300}>Limited availability</Text>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div
                                    style={{
                                        width: '16px',
                                        height: '16px',
                                        backgroundColor: '#fdf2f2',
                                        border: '1px solid #f87171',
                                        borderRadius: '4px',
                                    }}
                                />
                                <Text size={300}>Unavailable</Text>
                            </div>
                        </div>
                    </div>
                </Card>

                {/* Availability Blocks */}
                <Card style={{ padding: '20px', height: 'fit-content' }}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            marginBottom: '16px',
                        }}
                    >
                        <Clock24Regular style={{ fontSize: '20px', color: '#0078d4' }} />
                        <Text size={600} weight="semibold">
                            Availability Blocks
                        </Text>
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px',
                            maxHeight: '400px',
                            overflowY: 'auto',
                        }}
                    >
                        {availabilityBlocks.length === 0 ? (
                            <Text
                                size={400}
                                style={{ color: '#605e5c', textAlign: 'center', padding: '20px' }}
                            >
                                No custom availability blocks set. Using standard working hours.
                            </Text>
                        ) : (
                            availabilityBlocks.map((block) => (
                                <div
                                    key={block.id}
                                    style={{
                                        border: '1px solid #e1e5e9',
                                        borderRadius: '8px',
                                        padding: '16px',
                                        backgroundColor: '#f8f9fa',
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                        }}
                                    >
                                        <div style={{ flex: 1 }}>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '12px',
                                                    marginBottom: '8px',
                                                }}
                                            >
                                                <Text size={500} weight="medium">
                                                    {formatDate(block.date)}
                                                </Text>
                                                <Badge
                                                    appearance="tint"
                                                    color={
                                                        availabilityTypes.find(
                                                            (t) => t.value === block.type
                                                        )?.color as any
                                                    }
                                                >
                                                    {
                                                        availabilityTypes.find(
                                                            (t) => t.value === block.type
                                                        )?.label
                                                    }
                                                </Badge>
                                            </div>
                                            <Text size={400} style={{ color: '#605e5c' }}>
                                                {block.startTime} - {block.endTime}
                                                {block.maxConsultations &&
                                                    ` • Max ${block.maxConsultations} consultations`}
                                            </Text>
                                            {block.reason && (
                                                <Text
                                                    size={300}
                                                    style={{ color: '#605e5c', marginTop: '4px' }}
                                                >
                                                    {block.reason}
                                                </Text>
                                            )}
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <Button
                                                appearance="subtle"
                                                size="small"
                                                icon={<Edit24Regular />}
                                                onClick={() => setEditingBlock(block)}
                                            />
                                            <Button
                                                appearance="subtle"
                                                size="small"
                                                icon={<Delete24Regular />}
                                                onClick={() => handleDeleteAvailability(block.id)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </Card>
            </div>

            {/* Time Off Requests */}
            <Card style={{ padding: '20px', marginTop: '24px' }}>
                <Text size={600} weight="semibold" style={{ marginBottom: '16px' }}>
                    Time Off Requests
                </Text>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {timeOffRequests.length === 0 ? (
                        <Text
                            size={400}
                            style={{ color: '#605e5c', textAlign: 'center', padding: '20px' }}
                        >
                            No time off requests.
                        </Text>
                    ) : (
                        timeOffRequests.map((request) => (
                            <div
                                key={request.id}
                                style={{
                                    border: '1px solid #e1e5e9',
                                    borderRadius: '8px',
                                    padding: '16px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                            >
                                <div>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            marginBottom: '4px',
                                        }}
                                    >
                                        <Text size={500} weight="medium">
                                            {formatDate(request.startDate)} -{' '}
                                            {formatDate(request.endDate)}
                                        </Text>
                                        {getStatusBadge(request.status)}
                                        <Badge appearance="outline">
                                            {
                                                timeOffTypes.find((t) => t.value === request.type)
                                                    ?.label
                                            }
                                        </Badge>
                                    </div>
                                    <Text size={400} style={{ color: '#605e5c' }}>
                                        {request.reason}
                                    </Text>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </Card>

            {/* Add Availability Dialog */}
            <Dialog open={showAddDialog} onOpenChange={(_, data) => setShowAddDialog(data.open)}>
                <DialogSurface>
                    <DialogBody>
                        <DialogTitle>Add Availability Block</DialogTitle>
                        <DialogContent>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                <Field label="Date" required>
                                    <Input
                                        type="date"
                                        value={newBlock.date}
                                        onChange={(_, data) =>
                                            setNewBlock((prev) => ({ ...prev, date: data.value }))
                                        }
                                    />
                                </Field>

                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr',
                                        gap: '16px',
                                    }}
                                >
                                    <Field label="Start Time" required>
                                        <Input
                                            type="time"
                                            value={newBlock.startTime}
                                            onChange={(_, data) =>
                                                setNewBlock((prev) => ({
                                                    ...prev,
                                                    startTime: data.value,
                                                }))
                                            }
                                        />
                                    </Field>

                                    <Field label="End Time" required>
                                        <Input
                                            type="time"
                                            value={newBlock.endTime}
                                            onChange={(_, data) =>
                                                setNewBlock((prev) => ({
                                                    ...prev,
                                                    endTime: data.value,
                                                }))
                                            }
                                        />
                                    </Field>
                                </div>

                                <Field label="Availability Type" required>
                                    <Dropdown
                                        value={newBlock.type}
                                        onOptionSelect={(_, data) =>
                                            setNewBlock((prev) => ({
                                                ...prev,
                                                type: data.optionValue as any,
                                            }))
                                        }
                                    >
                                        {availabilityTypes.map((type) => (
                                            <Option key={type.value} value={type.value}>
                                                {type.label}
                                            </Option>
                                        ))}
                                    </Dropdown>
                                </Field>

                                {newBlock.type === 'limited' && (
                                    <Field label="Max Consultations">
                                        <Input
                                            type="number"
                                            min={1}
                                            max={20}
                                            value={newBlock.maxConsultations?.toString() || ''}
                                            onChange={(_, data) =>
                                                setNewBlock((prev) => ({
                                                    ...prev,
                                                    maxConsultations: parseInt(data.value) || 1,
                                                }))
                                            }
                                        />
                                    </Field>
                                )}

                                <Field label="Reason">
                                    <Input
                                        value={newBlock.reason}
                                        placeholder="Optional reason for this availability"
                                        onChange={(_, data) =>
                                            setNewBlock((prev) => ({ ...prev, reason: data.value }))
                                        }
                                    />
                                </Field>

                                <Field label="Notes">
                                    <Textarea
                                        value={newBlock.notes}
                                        placeholder="Additional notes..."
                                        onChange={(_, data) =>
                                            setNewBlock((prev) => ({ ...prev, notes: data.value }))
                                        }
                                    />
                                </Field>
                            </div>
                        </DialogContent>
                        <DialogActions>
                            <Button appearance="secondary" onClick={() => setShowAddDialog(false)}>
                                Cancel
                            </Button>
                            <Button appearance="primary" onClick={handleAddAvailability}>
                                Add Availability
                            </Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>

            {/* Time Off Request Dialog */}
            <Dialog
                open={showTimeOffDialog}
                onOpenChange={(_, data) => setShowTimeOffDialog(data.open)}
            >
                <DialogSurface>
                    <DialogBody>
                        <DialogTitle>Request Time Off</DialogTitle>
                        <DialogContent>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr',
                                        gap: '16px',
                                    }}
                                >
                                    <Field label="Start Date" required>
                                        <Input
                                            type="date"
                                            value={newTimeOff.startDate}
                                            onChange={(_, data) =>
                                                setNewTimeOff((prev) => ({
                                                    ...prev,
                                                    startDate: data.value,
                                                }))
                                            }
                                        />
                                    </Field>

                                    <Field label="End Date" required>
                                        <Input
                                            type="date"
                                            value={newTimeOff.endDate}
                                            onChange={(_, data) =>
                                                setNewTimeOff((prev) => ({
                                                    ...prev,
                                                    endDate: data.value,
                                                }))
                                            }
                                        />
                                    </Field>
                                </div>

                                <Field label="Type" required>
                                    <Dropdown
                                        value={newTimeOff.type}
                                        onOptionSelect={(_, data) =>
                                            setNewTimeOff((prev) => ({
                                                ...prev,
                                                type: data.optionValue as any,
                                            }))
                                        }
                                    >
                                        {timeOffTypes.map((type) => (
                                            <Option key={type.value} value={type.value}>
                                                {type.label}
                                            </Option>
                                        ))}
                                    </Dropdown>
                                </Field>

                                <Field label="Reason" required>
                                    <Textarea
                                        value={newTimeOff.reason}
                                        placeholder="Please provide a reason for your time off request..."
                                        onChange={(_, data) =>
                                            setNewTimeOff((prev) => ({
                                                ...prev,
                                                reason: data.value,
                                            }))
                                        }
                                    />
                                </Field>
                            </div>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                appearance="secondary"
                                onClick={() => setShowTimeOffDialog(false)}
                            >
                                Cancel
                            </Button>
                            <Button appearance="primary" onClick={handleRequestTimeOff}>
                                Submit Request
                            </Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>

            {/* Edit Availability Dialog */}
            {editingBlock && (
                <Dialog open={!!editingBlock} onOpenChange={() => setEditingBlock(null)}>
                    <DialogSurface>
                        <DialogBody>
                            <DialogTitle>Edit Availability Block</DialogTitle>
                            <DialogContent>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '16px',
                                    }}
                                >
                                    <Field label="Date" required>
                                        <Input
                                            type="date"
                                            value={editingBlock.date}
                                            onChange={(_, data) =>
                                                setEditingBlock((prev) =>
                                                    prev ? { ...prev, date: data.value } : null
                                                )
                                            }
                                        />
                                    </Field>

                                    <div
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr 1fr',
                                            gap: '16px',
                                        }}
                                    >
                                        <Field label="Start Time" required>
                                            <Input
                                                type="time"
                                                value={editingBlock.startTime}
                                                onChange={(_, data) =>
                                                    setEditingBlock((prev) =>
                                                        prev
                                                            ? { ...prev, startTime: data.value }
                                                            : null
                                                    )
                                                }
                                            />
                                        </Field>

                                        <Field label="End Time" required>
                                            <Input
                                                type="time"
                                                value={editingBlock.endTime}
                                                onChange={(_, data) =>
                                                    setEditingBlock((prev) =>
                                                        prev
                                                            ? { ...prev, endTime: data.value }
                                                            : null
                                                    )
                                                }
                                            />
                                        </Field>
                                    </div>

                                    <Field label="Availability Type" required>
                                        <Dropdown
                                            value={editingBlock.type}
                                            onOptionSelect={(_, data) =>
                                                setEditingBlock((prev) =>
                                                    prev
                                                        ? { ...prev, type: data.optionValue as any }
                                                        : null
                                                )
                                            }
                                        >
                                            {availabilityTypes.map((type) => (
                                                <Option key={type.value} value={type.value}>
                                                    {type.label}
                                                </Option>
                                            ))}
                                        </Dropdown>
                                    </Field>

                                    {editingBlock.type === 'limited' && (
                                        <Field label="Max Consultations">
                                            <Input
                                                type="number"
                                                min={1}
                                                max={20}
                                                value={
                                                    editingBlock.maxConsultations?.toString() || ''
                                                }
                                                onChange={(_, data) =>
                                                    setEditingBlock((prev) =>
                                                        prev
                                                            ? {
                                                                  ...prev,
                                                                  maxConsultations:
                                                                      parseInt(data.value) || 1,
                                                              }
                                                            : null
                                                    )
                                                }
                                            />
                                        </Field>
                                    )}

                                    <Field label="Reason">
                                        <Input
                                            value={editingBlock.reason || ''}
                                            placeholder="Optional reason for this availability"
                                            onChange={(_, data) =>
                                                setEditingBlock((prev) =>
                                                    prev ? { ...prev, reason: data.value } : null
                                                )
                                            }
                                        />
                                    </Field>

                                    <Field label="Notes">
                                        <Textarea
                                            value={editingBlock.notes || ''}
                                            placeholder="Additional notes..."
                                            onChange={(_, data) =>
                                                setEditingBlock((prev) =>
                                                    prev ? { ...prev, notes: data.value } : null
                                                )
                                            }
                                        />
                                    </Field>
                                </div>
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    appearance="secondary"
                                    onClick={() => setEditingBlock(null)}
                                >
                                    Cancel
                                </Button>
                                <Button appearance="primary" onClick={handleUpdateAvailability}>
                                    Update
                                </Button>
                            </DialogActions>
                        </DialogBody>
                    </DialogSurface>
                </Dialog>
            )}
        </div>
    );
};

export default DoctorAvailability;
